import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getNewsAdmin, saveNews, deleteNews } from '../api';
import { useAuth } from '../context/AuthContext';
import SearchInput from '../components/SearchInput';
import ConfirmDialog from '../components/ConfirmDialog';
import LoadingState from '../components/LoadingState';
import PortalIcon from '../components/PortalIcon';
import { Svg, Modal, useSort, sortBy, SortHead } from '../components/PortalUI';
import '../components/PortalUI.css';

const TYPES = ['Ny funktion', 'Forbedring', 'Rettelse'];
const TYPE_TONE = { 'Ny funktion': 'new', Forbedring: 'chg', Rettelse: 'ok' };
const STATUS = { published: ['Udgivet', 'ok'], planned: ['Planlagt', 'warn'], draft: ['Kladde', ''] };
const dateText = (iso) => (iso ? new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const todayIso = () => new Date().toISOString().slice(0, 10);

// Nyheder in the Provider portal: written by hand, shown to every customer. Only NordComply with the permission.
export default function NewsAdmin() {
  const { permissions, isOwner } = useAuth();
  const allowed = isOwner || permissions.includes('news');
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [sort, toggleSort] = useSort('date', 'desc');

  async function load() {
    try { setItems((await getNewsAdmin()).items); } catch (err) { setError(err.message); }
  }
  useEffect(() => { if (allowed) load(); }, [allowed]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = (items || []).filter((n) => (filter === 'all' || n.status === filter) && (!q || `${n.title} ${n.body}`.toLowerCase().includes(q)));
    return sortBy(rows, sort, { date: (n) => (n.publishAt ? new Date(n.publishAt).getTime() : null), type: (n) => n.type, title: (n) => n.title, status: (n) => STATUS[n.status]?.[0], by: (n) => n.createdByName });
  }, [items, search, filter, sort]);

  if (!allowed) return <Navigate to="/organizations" replace />;

  return (
    <div className="pu-page">
      <div className="pu-head">
        <div>
          <h1>Nyheder</h1>
          <p>Det, kunderne ser under Nyheder i deres portal. Kun NordComply kan oprette og rette. Skriv kun det, kunderne har glæde af, ikke det, der sker i baggrunden.</p>
        </div>
        <button type="button" className="pu-btn pu-btn--p" onClick={() => setEditing({})}><Svg name="news" />Ny nyhed</button>
      </div>
      {error && <p className="state-error">{error}</p>}
      {!items && !error && <LoadingState />}
      {items && (
        <>
          <div className="pu-bar">
            <SearchInput value={search} onChange={setSearch} placeholder="Søg i nyheder..." />
            <div className="pu-seg" role="group" aria-label="Status">
              {[['all', 'Alle'], ['published', 'Udgivet'], ['planned', 'Planlagt'], ['draft', 'Kladde']].map(([k, l]) => (
                <button key={k} type="button" className={filter === k ? 'is-on' : ''} onClick={() => setFilter(k)}>{l}</button>
              ))}
            </div>
          </div>
          {shown.length === 0 ? (
            <div className="pu-empty"><p>{items.length === 0 ? 'Ingen nyheder endnu.' : 'Ingen nyheder matcher.'}</p></div>
          ) : (
            <div className="pu-table">
              <div className="pu-th pu-tr pu-tr--n">
                <SortHead label="Dato" k="date" sort={sort} onSort={toggleSort} />
                <SortHead label="Type" k="type" sort={sort} onSort={toggleSort} />
                <SortHead label="Titel" k="title" sort={sort} onSort={toggleSort} />
                <SortHead label="Status" k="status" sort={sort} onSort={toggleSort} />
                <SortHead label="Oprettet af" k="by" sort={sort} onSort={toggleSort} />
                <span />
              </div>
              {shown.map((n) => {
                const [stText, stTone] = STATUS[n.status] || STATUS.draft;
                return (
                  <div key={n.id} className="pu-tr pu-tr--n pu-tr--click" role="button" tabIndex={0} onClick={() => setEditing(n)} onKeyDown={(e) => { if (e.key === 'Enter') setEditing(n); }}>
                    <span className="pu-c pu-c--m">{dateText(n.publishAt)}</span>
                    <span><span className={`pu-chip pu-chip--${TYPE_TONE[n.type] || 'chg'}`}>{n.type}</span></span>
                    <span className="pu-who"><span><b>{n.title}</b><small>{n.body}</small></span></span>
                    <span><span className={`pu-st${stTone ? ` pu-st--${stTone}` : ''}`}><i />{stText}</span></span>
                    <span className="pu-c pu-c--m">{n.createdByName || n.createdBy}</span>
                    <span className="pu-acts">
                      <button type="button" className="pu-ib" title="Rediger" aria-label="Rediger" onClick={(e) => { e.stopPropagation(); setEditing(n); }}><PortalIcon name="edit" /></button>
                      <button type="button" className="pu-ib pu-ib--del" title="Slet" aria-label="Slet" onClick={(e) => { e.stopPropagation(); setRemoving(n); }}><PortalIcon name="trash" /></button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {editing && <NewsEditor item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {removing && (
        <ConfirmDialog
          title="Slet nyhed"
          message={`Slet "${removing.title}"? Den forsvinder hos alle kunder.`}
          confirmLabel="Slet"
          onCancel={() => setRemoving(null)}
          onConfirm={async () => { await deleteNews(removing.id); setRemoving(null); load(); }}
        />
      )}
    </div>
  );
}

function NewsEditor({ item, onClose, onSaved }) {
  const { user } = useAuth();
  // The first name of whoever wrote it (kept on the item when it was created) is shown to the customers.
  const author = (item.createdByName || user?.name || '').trim().split(/s+/)[0] || '';
  const isNew = !item.id;
  const [title, setTitle] = useState(item.title || '');
  const [type, setType] = useState(item.type || TYPES[0]);
  const [body, setBody] = useState(item.body || '');
  const [status, setStatus] = useState(item.status || 'published');
  const [date, setDate] = useState(item.status === 'planned' && item.publishAt ? item.publishAt.slice(0, 10) : todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save(e) {
    e.preventDefault();
    setError('');
    if (!title.trim() || !body.trim()) { setError('Skriv en titel og en beskrivelse.'); return; }
    if (status === 'planned' && date < todayIso()) { setError('Vælg en dato fra i dag.'); return; }
    setSaving(true);
    try {
      await saveNews(isNew ? 'POST' : 'PATCH', { id: item.id, title, type, body, status, publishDate: date });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const when = status === 'planned' ? new Date(`${date}T06:00:00Z`).toISOString() : new Date().toISOString();
  return (
    <Modal
      size="l"
      onClose={onClose}
      busy={saving}
      head={(
        <>
          <span className="pu-ico"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h13v14H6a2 2 0 01-2-2zM17 9h3v8a2 2 0 01-2 2M8 9h5M8 13h5" /></svg></span>
          <div><h2>{isNew ? 'Ny nyhed' : 'Ret nyhed'}</h2><small>Vises for alle kunder under Nyheder, når den er udgivet.</small></div>
        </>
      )}
    >
      <form onSubmit={save}>
        <div className="pu-mb">
          <div className="pu-grid2">
            <div className="pu-col">
              <div className="pu-f"><label>Titel</label><input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} disabled={saving} autoFocus /></div>
              <div className="pu-f">
                <span className="pu-lab">Type</span>
                <div className="pu-seg pu-seg--w">{TYPES.map((x) => (<button key={x} type="button" className={type === x ? 'is-on' : ''} onClick={() => setType(x)} disabled={saving}>{x}</button>))}</div>
              </div>
              <div className="pu-f">
                <label>Beskrivelse <em>{body.length} / 600</em></label>
                <textarea className="pu-ta" rows={4} value={body} maxLength={600} onChange={(e) => setBody(e.target.value)} disabled={saving} />
                <em>Skriv, hvad kunden kan bruge det til. Linjeskift og tomme linjer bevares.</em>
              </div>
              <div className="pu-f">
                <span className="pu-lab">Udgives</span>
                <div className="pu-seg pu-seg--w">{[['published', 'Nu'], ['planned', 'Planlagt'], ['draft', 'Kladde']].map(([k, l]) => (<button key={k} type="button" className={status === k ? 'is-on' : ''} onClick={() => setStatus(k)} disabled={saving}>{l}</button>))}</div>
                {status === 'planned' && <input type="date" value={date} min={todayIso()} onChange={(e) => setDate(e.target.value)} disabled={saving} />}
              </div>
            </div>
            <div className="pu-col">
              <div className="pu-f">
                <span className="pu-lab">Sådan ser kunden den</span>
                <div className="nw-prev">
                  <div className="nw-i nw-i--new">
                    <div className="nw-m"><span className={`pu-chip pu-chip--${TYPE_TONE[type] || 'chg'}`}>{type}</span><small>{dateText(when)}{author ? ` · ${author}` : ''}</small><u>Ny</u></div>
                    <b>{title || 'Titel'}</b>
                    <p>{body || 'Beskrivelsen står her.'}</p>
                  </div>
                </div>
              </div>
              <p className="pu-info">Kun NordComplys administratorer med rettigheden kan se og ændre nyheder. Kunderne kan kun læse dem. En kladde vises ikke.</p>
            </div>
          </div>
          {error && <p className="pu-msg pu-msg--bad">{error}</p>}
        </div>
        <div className="pu-mf">
          <span className="pu-sp" />
          <button type="button" className="pu-btn" onClick={onClose} disabled={saving}>Annullér</button>
          <button type="submit" className="pu-btn pu-btn--p" disabled={saving}>{saving ? 'Gemmer…' : status === 'draft' ? 'Gem kladde' : status === 'planned' ? 'Gem og planlæg' : 'Udgiv'}</button>
        </div>
      </form>
    </Modal>
  );
}
